/**
 * celebrateUnlock.js — Utilitaire de célébration pour le déblocage progressif
 * d'une séquence/section dans les livrets pédagogiques.
 *
 * Effet :
 *  - Salve initiale centrale (confettis) + salves latérales façon feu d'artifice.
 *  - Toast pédagogique adapté (nom + couleur de la séquence débloquée).
 *
 * Usage :
 *  celebrateUnlock({
 *    title: 'S2 · Je découvre la Topolino',
 *    message: 'Bravo, tu passes à la séquence suivante 🎉',
 *    color: '#10b981',        // couleur d'accent
 *    durationMs: 2600,        // durée du feu d'artifice
 *    accentColors: [...],     // palette confettis facultative
 *  })
 *
 * Import :
 *  import { celebrateUnlock } from '@/utils/celebrateUnlock';
 */
import { toast } from 'sonner';

const DEFAULT_COLORS = ['#fbbf24', '#f59e0b', '#facc15', '#10b981', '#22d3ee', '#ffffff'];

/**
 * Déclenche une animation confettis + toast de célébration.
 * Retourne la promesse de l'import dynamique (utile pour tests / await).
 */
export function celebrateUnlock({
  title = 'Séquence débloquée !',
  message = 'Continue comme ça, la suite se débloque 🎉',
  color,
  durationMs = 2600,
  accentColors,
} = {}) {
  // Toast pédagogique immédiat
  toast.success(title, {
    description: message,
    duration: 4200,
  });

  // Palette : mix couleur d'accent + palette par défaut si fournie
  const colors = accentColors || (color ? [color, ...DEFAULT_COLORS] : DEFAULT_COLORS);

  // Feu d'artifice via canvas-confetti (import dynamique = pas de coût si non utilisé)
  return import('canvas-confetti').then(({ default: confetti }) => {
    // Salve initiale centrale
    confetti({
      particleCount: 130,
      spread: 95,
      origin: { y: 0.4 },
      colors,
      scalar: 1.05,
    });

    // Salves latérales continues pendant durationMs
    const end = Date.now() + durationMs;
    let cancelled = false;
    (function frame() {
      if (cancelled || Date.now() > end) return;
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 55,
        startVelocity: 50,
        origin: { x: 0, y: 0.85 },
        colors,
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 55,
        startVelocity: 50,
        origin: { x: 1, y: 0.85 },
        colors,
      });
      requestAnimationFrame(frame);
    })();

    return () => { cancelled = true; };
  }).catch(() => {
    // canvas-confetti indispo → toast reste, silencieux sur l'erreur
  });
}

export default celebrateUnlock;
